
import { supabase } from '@/integrations/supabase/client';
import { HeroContent } from '@/types/hero';
import { toast } from 'sonner';

export const getHeroContent = async (page: string): Promise<HeroContent | null> => {
  // Use a type assertion to tell TypeScript this is a valid table
  const { data, error } = await supabase
    .from('hero_content')
    .select('*')
    .eq('page', page)
    .maybeSingle(); // Use maybeSingle instead of single to handle case when no rows are returned
  
  if (error) {
    // If no data found, don't throw an error
    if (error.code === 'PGRST116') {
      return null;
    }
    console.error('Error fetching hero content:', error);
    throw error;
  }
  
  return data as HeroContent;
};

export const getAllHeroContent = async (): Promise<HeroContent[]> => {
  const { data, error } = await supabase
    .from('hero_content')
    .select('*')
    .order('page');
  
  if (error) {
    console.error('Error fetching all hero content:', error);
    throw error;
  }
  
  return (data || []) as HeroContent[];
};

export const saveHeroContent = async (content: HeroContent): Promise<HeroContent> => {
  try {
    // Check if the content already exists
    const { data: existingData, error: checkError } = await supabase
      .from('hero_content')
      .select('id')
      .eq('page', content.page)
      .maybeSingle(); // Use maybeSingle instead of single
    
    if (checkError && checkError.code !== 'PGRST116') {
      console.error('Error checking hero content:', checkError);
      throw checkError;
    }
    
    let result;
    
    if (existingData) {
      // Update existing content
      const { data, error } = await supabase
        .from('hero_content')
        .update({
          title: content.title,
          subtitle: content.subtitle,
          primary_button_text: content.primary_button_text,
          primary_button_link: content.primary_button_link,
          secondary_button_text: content.secondary_button_text,
          secondary_button_link: content.secondary_button_link,
          background_image: content.background_image,
          media_type: content.media_type,
          video_url: content.video_url,
          carousel_images: content.carousel_images
        })
        .eq('id', existingData.id)
        .select('*')
        .maybeSingle();
      
      if (error) {
        console.error('Error updating hero content:', error);
        throw error;
      }
      
      result = { data, error };
    } else {
      // Insert new content
      const { data, error } = await supabase
        .from('hero_content')
        .insert({
          page: content.page,
          title: content.title,
          subtitle: content.subtitle,
          primary_button_text: content.primary_button_text,
          primary_button_link: content.primary_button_link,
          secondary_button_text: content.secondary_button_text,
          secondary_button_link: content.secondary_button_link,
          background_image: content.background_image,
          media_type: content.media_type,
          video_url: content.video_url,
          carousel_images: content.carousel_images
        })
        .select('*')
        .maybeSingle();
      
      if (error) {
        console.error('Error inserting hero content:', error);
        throw error;
      }
      
      result = { data, error };
    }
    
    if (result.error) {
      console.error('Error saving hero content:', result.error);
      throw result.error;
    }
    
    return result.data as HeroContent;
  } catch (error) {
    console.error('Error in saveHeroContent:', error);
    throw error;
  }
};

export const uploadHeroImage = async (file: File): Promise<string> => {
  try {
    // Generate a unique filename
    const fileName = `hero-${Date.now()}.${file.name.split('.').pop()}`;
    
    // Upload the file to the hero-images bucket
    const { data, error } = await supabase.storage
      .from('hero-images')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      });
    
    if (error) {
      console.error('Error uploading hero image:', error);
      toast.error('Failed to upload image. Please try again.');
      throw error;
    }
    
    // Get the public URL of the uploaded file
    const { data: urlData } = supabase.storage
      .from('hero-images')
      .getPublicUrl(fileName);
    
    console.log('Hero image uploaded successfully:', urlData.publicUrl);
    return urlData.publicUrl;
  } catch (error) {
    console.error('Error in uploadHeroImage:', error);
    throw error;
  }
};

export const getHeroImages = async (): Promise<{ name: string; url: string; size: number; created_at: string }[]> => {
  try {
    console.log('Fetching images from hero-images bucket...');
    const { data, error } = await supabase.storage
      .from('hero-images')
      .list('', {
        limit: 100,
        offset: 0,
        sortBy: { column: 'created_at', order: 'desc' }
      });
    
    console.log('Supabase storage response:', { data, error });
    
    if (error) {
      console.error('Error fetching hero images:', error);
      throw error;
    }
    
    if (!data) {
      console.log('No data returned from storage');
      return [];
    }
    
    // Filter for image files and get public URLs
    const imageFiles = data
      .filter(file => {
        const extension = file.name.toLowerCase().split('.').pop();
        const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension || '');
        console.log(`File: ${file.name}, Extension: ${extension}, IsImage: ${isImage}`);
        return isImage;
      })
      .map(file => {
        const { data: urlData } = supabase.storage
          .from('hero-images')
          .getPublicUrl(file.name);
        
        return {
          name: file.name,
          url: urlData.publicUrl,
          size: file.metadata?.size || 0,
          created_at: file.created_at || new Date().toISOString()
        };
      });
    
    console.log('Processed image files:', imageFiles);
    return imageFiles;
  } catch (error) {
    console.error('Error in getHeroImages:', error);
    throw error;
  }
};

export const deleteHeroImage = async (fileName: string): Promise<void> => {
  try {
    const { error } = await supabase.storage
      .from('hero-images')
      .remove([fileName]);
    
    if (error) {
      console.error('Error deleting hero image:', error);
      throw error;
    }
    
    console.log('Hero image deleted successfully:', fileName);
  } catch (error) {
    console.error('Error in deleteHeroImage:', error);
    throw error;
  }
};